import { useEffect, useRef, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { ApolloError, useLazyQuery } from "@apollo/client";

import { StackVendorFormFields } from "../../types";
import { CHECK_TERRAGRUNT_VERSION } from "./gql";

const DEBOUNCE_TIMEOUT = 300;

const useAsyncTerragruntVersionValidation = (
  setAsyncValidationLoading: (isLoading: boolean) => void
) => {
  const { setError, getValues, formState, clearErrors } = useFormContext<StackVendorFormFields>();

  const terragruntError = formState.errors.terragrunt?.terragruntVersion;
  const terraformError = formState.errors.terragrunt?.workflowVersion;
  const timeout = useRef<NodeJS.Timeout | undefined>(undefined);

  const stopLoading = useCallback(() => {
    if (!timeout.current) {
      setAsyncValidationLoading(false);
    }
  }, [setAsyncValidationLoading, timeout]);

  const onTerragruntQueryError = useCallback(
    (error: ApolloError) => {
      stopLoading();
      if ((!terragruntError || terragruntError.type === "custom") && !terraformError) {
        setError("terragrunt.terragruntVersion", {
          type: "custom",
          message: error.message,
        });
      }
    },
    [stopLoading, terragruntError, terraformError, setError]
  );

  const onTerragruntQueryComplete = useCallback(() => {
    stopLoading();
    if (terragruntError && terragruntError.type === "custom") {
      clearErrors("terragrunt.terragruntVersion");
    }
  }, [terragruntError, clearErrors, stopLoading]);

  const [checkTerragruntVersion, { data, error }] = useLazyQuery(CHECK_TERRAGRUNT_VERSION);

  useEffect(() => {
    if (error) {
      onTerragruntQueryError(error);
    }
  }, [error, onTerragruntQueryError]);

  useEffect(() => {
    if (data?.terragruntEffectiveVersion) {
      onTerragruntQueryComplete();
    }
  }, [data?.terragruntEffectiveVersion, onTerragruntQueryComplete]);

  const values = getValues();
  const terragruntValues = values.terragrunt;

  useEffect(() => {
    if (
      terragruntValues.tool &&
      terragruntValues.terragruntVersion.value &&
      terragruntValues.workflowVersion.value
    ) {
      setAsyncValidationLoading(true);
      timeout.current = setTimeout(() => {
        if (terragruntError && terragruntError.type === "custom") {
          clearErrors("terragrunt.terragruntVersion");
        }
        checkTerragruntVersion({
          variables: {
            terragruntConstraints: terragruntValues.terragruntVersion.value,
            terraformConstraints: terragruntValues.workflowVersion.value,
            tool: terragruntValues.tool,
          },
        });
        timeout.current = undefined;
      }, DEBOUNCE_TIMEOUT);
    }

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = undefined;
      }

      setAsyncValidationLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terragruntValues.terragruntVersion, terragruntValues.workflowVersion]);
};

export default useAsyncTerragruntVersionValidation;
